import { API } from 'aws-amplify';
import * as mutations from '../graphql/virtualTryOn/mutations';

/**
 * Virtual Try-On API Helper
 * Handles virtual try-on generation with caching and credit management
 */

/**
 * Extract S3 key from full S3 URL
 * Example: https://bucket.s3.amazonaws.com/path/to/file.jpg -> path/to/file.jpg
 * @param {string} s3Url - Full S3 URL
 * @returns {string} - S3 key path
 */
const extractS3Key = s3Url => {
  try {
    const url = new URL(decodeURIComponent(s3Url));
    // Remove leading slash from pathname
    return url.pathname.substring(1);
  } catch (error) {
    console.error('[Virtual Try-On] Failed to parse S3 URL:', s3Url, error);
    // If it's not a URL, assume it's already a key
    return s3Url;
  }
};

/**
 * Try on an outfit from a Likiee on a user's photo
 * @param {string} likieeId - ID of the Likiee
 * @param {string} likieeImageUrl - Full S3 URL of outfit image
 * @param {string} userPhotoPath - Path from photo upload (e.g., "full-body-front/photo.png")
 * @param {string} outfitJson - Optional pre-analyzed outfit JSON for faster processing
 * @returns {Promise<object>} - Result with imageUrl, cached status, credits charged, etc.
 */
export const tryOnOutfit = async (likieeId, likieeImageUrl, userPhotoPath, outfitJson = null) => {
  try {
    // Extract S3 keys from URLs
    const productImageKey = extractS3Key(likieeImageUrl);

    const input = {
      likieeId,
      likieeImageUrl: productImageKey, // Send S3 key instead of full URL
      userPhotoPath,
    };

    // Add outfitJson if provided (for faster processing)
    if (outfitJson) {
      input.outfitJson = typeof outfitJson === 'string' ? outfitJson : JSON.stringify(outfitJson);
    }

    const result = await API.graphql({
      query: mutations.virtualTryOn,
      variables: { input },
    });

    const { virtualTryOn } = result.data;

    if (!virtualTryOn.success) {
      throw new Error(virtualTryOn.error || 'Virtual try-on failed');
    }

    return {
      imageUrl: virtualTryOn.generatedImageUrl,
      s3Key: virtualTryOn.s3Key,
      isCached: virtualTryOn.cached,
      creditsCharged: virtualTryOn.creditsCharged,
      processingTime: virtualTryOn.processingTime,
      metadata: virtualTryOn.metadata ? JSON.parse(virtualTryOn.metadata) : null,
    };
  } catch (error) {
    // Handle specific error cases
    if (error.message.includes('Insufficient credits')) {
      throw new Error('INSUFFICIENT_CREDITS');
    } else if (error.message.includes('authentication')) {
      throw new Error('AUTHENTICATION_REQUIRED');
    } else if (error.message.includes('required')) {
      throw new Error('INVALID_INPUT');
    } else if (error.message.includes('key does not exist') || error.message.includes('NoSuchKey')) {
      throw new Error(
        'S3_KEY_NOT_FOUND: The specified product image or user photo was not found in S3. Please ensure the image exists.',
      );
    }

    throw error;
  }
};

/**
 * Regenerate try-on result (force new generation, always charges credits)
 * @param {string} likieeId - ID of the Likiee
 * @param {string} likieeImageUrl - Full S3 URL of outfit image
 * @param {string} userPhotoPath - Path from photo upload
 * @returns {Promise<object>} - Result with new imageUrl and credits charged
 */
export const regenerateTryOn = async (likieeId, likieeImageUrl, userPhotoPath) => {
  try {
    // Extract S3 keys from URLs
    const productImageKey = extractS3Key(likieeImageUrl);

    const result = await API.graphql({
      query: mutations.regenerateVirtualTryOn,
      variables: {
        input: {
          likieeId,
          likieeImageUrl: productImageKey, // Send S3 key instead of full URL
          userPhotoPath,
        },
      },
    });

    const { regenerateVirtualTryOn } = result.data;

    if (!regenerateVirtualTryOn.success) {
      throw new Error(regenerateVirtualTryOn.error || 'Regeneration failed');
    }

    return {
      imageUrl: regenerateVirtualTryOn.generatedImageUrl,
      s3Key: regenerateVirtualTryOn.s3Key,
      isCached: regenerateVirtualTryOn.cached,
      creditsCharged: regenerateVirtualTryOn.creditsCharged,
      processingTime: regenerateVirtualTryOn.processingTime,
      metadata: regenerateVirtualTryOn.metadata ? JSON.parse(regenerateVirtualTryOn.metadata) : null,
    };
  } catch (error) {
    console.error('[Virtual Try-On] Regeneration error:', error);

    if (error.message.includes('Insufficient credits')) {
      throw new Error('INSUFFICIENT_CREDITS');
    } else if (error.message.includes('key does not exist') || error.message.includes('NoSuchKey')) {
      throw new Error('S3_KEY_NOT_FOUND: The specified product image or user photo was not found in S3.');
    }

    throw error;
  }
};

/**
 * Get estimated credits cost for a try-on
 * @param {boolean} isCached - Whether result is expected to be cached
 * @returns {number} - Credits cost (0 if cached, 10 if new)
 */
export const getEstimatedCost = isCached => {
  return isCached ? 0 : 10;
};

/**
 * Get estimated processing time
 * @param {boolean} hasOutfitJson - Whether outfit JSON is pre-analyzed
 * @returns {object} - Min and max processing time in seconds
 */
export const getEstimatedProcessingTime = (hasOutfitJson = false) => {
  if (hasOutfitJson) {
    return { min: 15, max: 20 };
  }
  return { min: 20, max: 25 };
};
